/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import {
  useLazyGetAddressTypeQuery,
  useLazyGetCivilStatusQuery,
  useLazyGetMatrimonialRegimesQuery,
  useLazyGetPersonTypeQuery,
} from "@api/catalogs";
import { useLazyGetAddressQuery, usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { Address, AddressData, CivilStatus, MatrimonialRegimesData, PayloadPersonPatch } from "@interfaces/index";
import { InactivityHandler } from "@components/InactivityHandler";
import { ADDRESS_CODES, PERSON_CATALOG } from "@helpers/constantes";

import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { UUID } from "crypto";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError, PersonaStructure } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetPersonsQuery, usePatchPersonMutation, usePostPersonMutation } from "@api/personasApi";
import { CURP } from "@utils/expreciones";

const OTHER_PERSON_ID = "1-2-3-4-5";

const SolicitudCreditoPart1PFAE: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [getByCp] = useLazyGetByCpQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const [addresType, setAddresType] = useState<Address | undefined>(undefined);
  const [addresTypeParticular, setAddresTypeParticular] = useState<Address | undefined>(undefined);
  const [colonias, setColonias] = useState<{ id: number; name: string }[]>([]);
  const [coloniasParticular, setColoniasParticular] = useState<{ id: number; name: string }[]>([]);

  const [address, setAddress] = useState<AddressData | undefined>(undefined);
  const [addressParticular, setAddressParticular] = useState<AddressData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listEdoCivil, setListEdoCivil] = useState<CivilStatus[]>([]);
  const [listMatrimonialRegimen, setListMatrimonialRegimen] = useState<MatrimonialRegimesData[]>([]);
  const [idEdoCivilCasado, setIdEdoCivilCasado] = useState<string>("");
  const [idRepresentative, setIdRepresentative] = useState<string>("");
  const [otherPersons, setOtherPersons] = useState<PersonaStructure[]>([]);
  const [personTypeConyugeAval, setPersonTypeConyugeAval] = useState<{ id: string } | null>(null);

  const [getAddress] = useLazyGetAddressQuery();
  const [getAddresType] = useLazyGetAddressTypeQuery();
  const [getApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getPersonType] = useLazyGetPersonTypeQuery();
  const [getEstadoCivil] = useLazyGetCivilStatusQuery();
  const [getMatrimonialRegimen] = useLazyGetMatrimonialRegimesQuery();
  const [patchPerson] = usePatchPersonMutation();
  const [postPerson] = usePostPersonMutation();

  const formik = useFormik({
    initialValues: {
      // Domicilio principal
      codigoPostal: "",
      calle: "",
      noInterior: "",
      noExterior: "",
      estadoId: "",
      ciudadId: "",
      municipioId: "",
      coloniaId: "",
      estado: "",
      ciudad: "",
      municipio: "",
      otro: "",
      localidadId: "",
      localidad: "",

      // Datos personales
      estado_civil_id: "",
      matrimonial_regimes_id: "",
      conyuge_id: "",
      conyuge_name: "",
      conyuge_last_name: "",
      conyuge_last_name_2: "",
      curp: "",
      ocupacion: "",

      // Domicilio particular
      codigoPostal_particular: "",
      calle_particular: "",
      noInterior_particular: "",
      noExterior_particular: "",
      estadoId_particular: "",
      ciudadId_particular: "",
      municipioId_particular: "",
      coloniaId_particular: "",
      estado_particular: "",
      ciudad_particular: "",
      municipio_particular: "",
      otro_particular: "",
      localidadId_particular: "",
      localidad_particular: "",
    },
    validationSchema: yup.object({
      // Validaciones domicilio principal
      codigoPostal: yup
        .string()
        .required("Requerido")
        .matches(/^[0-9]{5}$/, "Debe ser un código postal válido"),
      calle: yup.string().required("Requerido"),
      noInterior: yup.string().notRequired(),
      noExterior: yup.string().required("Requerido"),
      estadoId: yup.string().required("Requerido"),
      ciudadId: yup.string(),
      municipioId: yup.string(),
      coloniaId: yup.string().required("Requerido"),
      otro: yup.string().when("coloniaId", {
        is: "-1",
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),

      // Datos personales
      estado_civil_id: yup.string().required("Campo requerido"),
      matrimonial_regimes_id: yup.string().when("estado_civil_id", {
        is: idEdoCivilCasado,
        then: (schema) => schema.required("Campo requerido"),
      }),
      curp: yup.string().required("Campo requerido").matches(CURP, "Formato de CURP no válido"),
      conyuge_id: yup.string().when("estado_civil_id", {
        is: idEdoCivilCasado,
        then: (schema) => schema.required("Campo requerido"),
      }),
      conyuge_name: yup.string().when("conyuge_id", {
        is: OTHER_PERSON_ID,
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),
      conyuge_last_name: yup.string().when("conyuge_id", {
        is: OTHER_PERSON_ID,
        then: (schema) => schema.required("Campo requerido"),
      }),
      conyuge_last_name_2: yup.string(),
      ocupacion: yup.string().required("Campo requerido"),

      // Validaciones domicilio particular
      codigoPostal_particular: yup
        .string()
        .required("Requerido")
        .matches(/^[0-9]{5}$/, "Debe ser un código postal válido"),
      calle_particular: yup.string().required("Requerido"),
      noInterior_particular: yup.string().notRequired(),
      noExterior_particular: yup.string().required("Requerido"),
      estadoId_particular: yup.string().required("Requerido"),
      ciudadId_particular: yup.string(),
      municipioId_particular: yup.string(),
      coloniaId_particular: yup.string().required("Requerido"),
      otro_particular: yup.string().when("coloniaId_particular", {
        is: "-1",
        then: (schema) => schema.required("Campo requerido"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        const payload_address = {
          client_details_id: clientDetails.id,
          address_type_id: addresType?.id,
          cp: values.codigoPostal,
          street: values.calle,
          no_ext: values.noExterior,
          no_int: values.noInterior,
          country_id: 1,
          state_id: values.estadoId ? +values.estadoId : null,
          city_id: values.ciudadId ? +values.ciudadId : null,
          colonia_id: Number(values.coloniaId) === -1 ? null : Number(values.coloniaId),
          other_neighborhood: values.otro,
          locality_id: values.localidadId ? +values.localidadId : null,
        };

        const payload_address_particular = {
          client_details_id: clientDetails.id,
          address_type_id: addresTypeParticular?.id,
          person_id: idRepresentative,
          cp: values.codigoPostal_particular,
          street: values.calle_particular,
          no_ext: values.noExterior_particular,
          no_int: values.noInterior_particular,
          country_id: 1,
          state_id: values.estadoId_particular ? +values.estadoId_particular : null,
          city_id: values.ciudadId_particular ? +values.ciudadId_particular : null,
          colonia_id: Number(values.coloniaId_particular) === -1 ? null : Number(values.coloniaId_particular),
          other_neighborhood: values.otro_particular,
          locality_id: values.localidadId_particular ? +values.localidadId_particular : null,
        };

        if (address?.id) {
          await patchAddress({
            addressId: address.id as UUID,
            data: payload_address,
          }).unwrap();
        } else {
          await postAddress(payload_address).unwrap();
        }

        if (addressParticular?.id) {
          await patchAddress({
            addressId: addressParticular?.id as UUID,
            data: payload_address_particular,
          }).unwrap();
        } else {
          await postAddress(payload_address_particular).unwrap();
        }

        const is_married = values.estado_civil_id === idEdoCivilCasado;
        let person_conyuge: PersonaStructure | null = null;

        if (is_married) {
          // Obtener lista de personas actualizada
          const list_persons = await getPersons({
            loanApplicationId: application.id,
          }).unwrap();

          // si es casado, se valida si se selecciono otra persona, si es asi, se crea un nuevo conyuge
          if (values.conyuge_id === OTHER_PERSON_ID) {
            if (!personTypeConyugeAval) {
              throw new Error("No se encontró el tipo de persona para cónyuge");
            }
            const response_create = await postPerson({
              body: {
                loan_application_id: application.id,
                person_types: [personTypeConyugeAval.id],
                name: values.conyuge_name,
                last_name: values.conyuge_last_name,
                last_name_2: values.conyuge_last_name_2,
              },
            }).unwrap();
            person_conyuge = response_create.data;
          } else {
            // obtener la informacion del conyuge
            person_conyuge = list_persons.find((item) => item.id === values.conyuge_id) || null;
            if (!person_conyuge) {
              throw new Error("No se encontró el cónyuge");
            }

            // obtener los tipos de persona del conyuge
            const person_types = person_conyuge.person_types_detail.map((item) => item.id);

            // validar si el conyuge tiene el tipo de persona 'conyuge aval', en caso de no tenerlo, se agrega
            if (!person_types.includes(personTypeConyugeAval?.id as UUID)) {
              if (!personTypeConyugeAval) {
                throw new Error("No se encontró el tipo de persona para cónyuge");
              }
              await patchPerson({
                personId: person_conyuge.id,
                body: {
                  person_types: [...person_types, personTypeConyugeAval.id],
                },
              }).unwrap();
            }
          }
        }

        const payload: PayloadPersonPatch = {
          civil_status_id: values.estado_civil_id,
          matrimonial_regimes_id: is_married ? values.matrimonial_regimes_id : null,
          spouse_id: is_married && person_conyuge ? person_conyuge.id : null,
          curp: values.curp,
          occupation: values.ocupacion,
        };

        await patchPerson({
          personId: idRepresentative,
          body: payload,
        }).unwrap();

        navigate("/producto/creditosimple/solicitud_credito_2PFAE");
      } catch (error) {
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    },
  });

  const savePersonalInformation = async (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    try {
      formik.handleBlur(e);

      const { estado_civil_id, matrimonial_regimes_id } = formik.errors;
      if (estado_civil_id || matrimonial_regimes_id) {
        return;
      }

      const payload: PayloadPersonPatch = {
        civil_status_id: formik.values.estado_civil_id,
        matrimonial_regimes_id: formik.values.estado_civil_id === idEdoCivilCasado ? matrimonial_regimes_id : null,
      };

      await patchPerson({
        personId: idRepresentative,
        body: payload,
      }).unwrap();
    } catch (error) {
      console.error("Error guardando información personal:", error);
    }
  };

  const saveOperativeAddress = async (e: React.FocusEvent<HTMLInputElement>) => {
    try {
      formik.handleBlur(e);
      if (!addresType || !clientDetails?.id) return;

      const {
        codigoPostal: codigoPostal_err,
        calle: calle_err,
        noExterior: noExterior_err,
        noInterior: noInterior_err,
        coloniaId: coloniaId_err,
      } = formik.errors;

      if (codigoPostal_err || calle_err || noExterior_err || noInterior_err || coloniaId_err) {
        return;
      }

      const payload_address = {
        client_details_id: clientDetails.id,
        address_type_id: addresType.id,
        cp: formik.values.codigoPostal,
        street: formik.values.calle,
        no_ext: formik.values.noExterior,
        no_int: formik.values.noInterior,
        country_id: 1,
        state_id: formik.values.estadoId ? +formik.values.estadoId : null,
        city_id: formik.values.ciudadId ? +formik.values.ciudadId : null,
        colonia_id: Number(formik.values.coloniaId) === -1 ? null : Number(formik.values.coloniaId),
        other_neighborhood: formik.values.otro,
      };

      if (address?.id) {
        await patchAddress({
          addressId: address.id as UUID,
          data: payload_address,
        }).unwrap();
      } else {
        const newAddress = await postAddress(payload_address).unwrap();
        setAddress(newAddress.data);
      }
    } catch (error) {
      console.error("Error guardando dirección operativa:", error);
    }
  };

  const savePersonalAddress = async (e: React.FocusEvent<HTMLInputElement>) => {
    try {
      formik.handleBlur(e);
      if (!addresTypeParticular || !clientDetails?.id) return;

      const {
        codigoPostal_particular: codigoPostal_err,
        calle_particular: calle_err,
        noExterior_particular: noExterior_err,
        noInterior_particular: noInterior_err,
        coloniaId_particular: coloniaId_err,
      } = formik.errors;

      if (codigoPostal_err || calle_err || noExterior_err || noInterior_err || coloniaId_err) {
        return;
      }

      const payload_address = {
        client_details_id: clientDetails.id,
        address_type_id: addresTypeParticular.id,
        person_id: idRepresentative,
        cp: formik.values.codigoPostal_particular,
        street: formik.values.calle_particular,
        no_ext: formik.values.noExterior_particular,
        no_int: formik.values.noInterior_particular,
        country_id: 1,
        state_id: formik.values.estadoId_particular ? +formik.values.estadoId_particular : null,
        city_id: formik.values.ciudadId_particular ? +formik.values.ciudadId_particular : null,
        colonia_id:
          Number(formik.values.coloniaId_particular) === -1 ? null : Number(formik.values.coloniaId_particular),
        other_neighborhood: formik.values.otro_particular,
      };

      if (addressParticular?.id) {
        await patchAddress({
          addressId: addressParticular.id as UUID,
          data: payload_address,
        }).unwrap();
      } else {
        const newAddress = await postAddress(payload_address).unwrap();
        setAddressParticular(newAddress.data);
      }
    } catch (error) {
      console.error("Error guardando dirección personal:", error);
    }
  };

  const fetchLocationData = async (codigoPostal: string, coloniaId?: number) => {
    try {
      if (codigoPostal.length === 5) {
        const value = await getByCp(codigoPostal).unwrap();
        const { colonias, cities, states, localities } = value;
        setColonias(colonias);

        formik.setFieldValue("estadoId", states[0]?.id || "");
        formik.setFieldValue("estado", states[0]?.name || "");
        formik.setFieldValue("ciudadId", cities[0]?.id || "");
        formik.setFieldValue("ciudad", cities[0]?.name || "");
        formik.setFieldValue("municipioId", localities[0]?.id || "");
        formik.setFieldValue("municipio", localities[0]?.name || "");
        formik.setFieldValue("localidadId", localities[0]?.id || "");
        formik.setFieldValue("localidad", localities[0]?.name || "");

        if (coloniaId) {
          const coloniaSelected = colonias.find((item: any) => item.id == coloniaId);
          formik.setFieldValue("coloniaId", coloniaSelected?.id || "");
        } else {
          formik.setFieldValue("coloniaId", colonias[0]?.id || "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } else {
        formik.setFieldValue("estadoId", "");
        formik.setFieldValue("estado", "");
        formik.setFieldValue("ciudadId", "");
        formik.setFieldValue("ciudad", "");
        formik.setFieldValue("municipioId", "");
        formik.setFieldValue("municipio", "");
        formik.setFieldValue("coloniaId", "");
        formik.setFieldValue("colonia", "");
        formik.setFieldValue("localidadId", "");
        formik.setFieldValue("localidad", "");
      }
    } catch (error) {
      console.error("Error fetching location data:", error);
    }
  };

  const fetchLocationDataParticular = async (codigoPostal: string, coloniaId?: number) => {
    try {
      if (codigoPostal.length === 5) {
        const value = await getByCp(codigoPostal).unwrap();
        const { colonias, cities, states, localities } = value;
        setColoniasParticular(colonias); // <- usa un state separado para particular

        formik.setFieldValue("estadoId_particular", states[0]?.id || "");
        formik.setFieldValue("estado_particular", states[0]?.name || "");
        formik.setFieldValue("ciudadId_particular", cities[0]?.id || "");
        formik.setFieldValue("ciudad_particular", cities[0]?.name || "");
        formik.setFieldValue("municipioId_particular", localities[0]?.id || "");
        formik.setFieldValue("municipio_particular", localities[0]?.name || "");
        formik.setFieldValue("localidadId_particular", localities[0]?.id || "");
        formik.setFieldValue("localidad_particular", localities[0]?.name || "");

        if (coloniaId) {
          const coloniaSelected = colonias.find((item: any) => item.id == coloniaId);
          formik.setFieldValue("coloniaId_particular", coloniaSelected?.id || "");
        } else {
          formik.setFieldValue("coloniaId_particular", colonias[0]?.id || "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } else {
        formik.setFieldValue("estadoId_particular", "");
        formik.setFieldValue("estado_particular", "");
        formik.setFieldValue("ciudadId_particular", "");
        formik.setFieldValue("ciudad_particular", "");
        formik.setFieldValue("municipioId_particular", "");
        formik.setFieldValue("municipio_particular", "");
        formik.setFieldValue("coloniaId_particular", "");
        formik.setFieldValue("colonia_particular", "");
        formik.setFieldValue("localidadId_particular", "");
        formik.setFieldValue("localidad_particular", "");
      }
    } catch (error) {
      console.error("Error fetching location data (particular):", error);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        if (!application) {
          throw new Error("Solicitud no encontrada");
        }

        const [
          loan_application,
          res_address_type,
          res_address,
          res_persons,
          res_person_type,
          res_edo_civil,
          res_edo_matrimonial,
        ] = await Promise.all([
          getApplicationById(application.id).unwrap(),
          getAddresType().unwrap(),
          getAddress({
            accountId: application.client_details_id,
          }).unwrap(),
          getPersons({
            loanApplicationId: application.id,
          }).unwrap(),
          getPersonType().unwrap(),
          getEstadoCivil().unwrap(),
          getMatrimonialRegimen().unwrap(),
        ]);

        if (!loan_application) {
          throw new Error("No se encontró la solicitud de crédito");
        }

        const address_type = res_address_type.find((item: Address) => item.code === ADDRESS_CODES.operativo.code);

        const address_type_particular = res_address_type.find(
          (item: Address) => item.code === ADDRESS_CODES.particular.code,
        );
        const address = res_address.find((item: AddressData) => item.address_type_id === address_type?.id);

        const edo_civil_casado: CivilStatus | undefined = res_edo_civil.find(
          (item) => item.name.toLowerCase() === "casado",
        );

        const person_type_conyuge = res_person_type.find((item) => item.code === PERSON_CATALOG.conyuge_aval.code);

        const person_type_representative = res_person_type.find(
          (item) => item.code === PERSON_CATALOG.representante_legal.code,
        );

        const person_representative = res_persons.find((item) =>
          item.person_types_detail.some((detail) => detail.id === person_type_representative?.id),
        );

        const other_persons = [];
        // obtener los ids de personas que ya son conyuges de algun aval
        const conyuges_ids = res_persons
          .filter((item) => item.id !== person_representative?.id)
          .map((item) => item.spouse_id);

        // obtener las personas diferentes al aval seleccionado y los conyuges
        for (const person of res_persons) {
          if (person.id !== person_representative?.id) {
            if (!conyuges_ids.includes(person.id)) {
              other_persons.push(person);
            }
          }
        }

        // agregar el conyugue "otros" a la lista de personas, este sirve para crear un nuevo conyuge
        const other_person = {
          id: OTHER_PERSON_ID,
          name: "Otro",
          last_name: "",
          last_name_2: "",
          person_types_detail: [],
        } as unknown as PersonaStructure;

        other_persons.push(other_person);

        const edo_civil_selected: CivilStatus | undefined = res_edo_civil.find(
          (item) => item.id === String(person_representative?.civil_status_id),
        );
        const edo_matrimonial_selected = res_edo_matrimonial.find(
          (item) => item.id === String(person_representative?.matrimonial_regimes_id),
        );

        const address_response_particular = await getAddress({
          personId: String(person_representative?.id),
        }).unwrap();

        const addressParticularPerson = address_response_particular.find(
          (item: AddressData) => item.address_type_id === address_type_particular?.id,
        );

        setAddresType(address_type);
        setAddresTypeParticular(address_type_particular);
        setAddress(address);
        setAddressParticular(addressParticularPerson);
        setIdEdoCivilCasado(String(edo_civil_casado?.id || ""));
        setIdRepresentative(String(person_representative?.id || ""));
        setPersonTypeConyugeAval(person_type_conyuge ? { id: person_type_conyuge.id } : null);
        setListEdoCivil(res_edo_civil);
        setListMatrimonialRegimen(res_edo_matrimonial);
        setOtherPersons(other_persons);

        if (address) {
          formik.setFieldValue("codigoPostal", address.cp || "");
          formik.setFieldValue("calle", address.street || "");
          formik.setFieldValue("noInterior", address.no_int || "");
          formik.setFieldValue("noExterior", address.no_ext || "");
          formik.setFieldValue("otro", address.other_neighborhood || "");
          fetchLocationData(address.cp, address.colonia_id);
        }

        if (addressParticularPerson) {
          formik.setFieldValue("codigoPostal_particular", addressParticularPerson.cp || "");
          formik.setFieldValue("calle_particular", addressParticularPerson.street || "");
          formik.setFieldValue("noInterior_particular", addressParticularPerson.no_int || "");
          formik.setFieldValue("noExterior_particular", addressParticularPerson.no_ext || "");
          formik.setFieldValue("otro_particular", addressParticularPerson.other_neighborhood || "");
          fetchLocationDataParticular(addressParticularPerson.cp, addressParticularPerson.colonia_id);
        }

        if (edo_civil_selected?.id) {
          formik.setFieldValue("estado_civil_id", edo_civil_selected?.id || "");
          formik.setFieldValue("matrimonial_regimes_id", edo_matrimonial_selected?.id || "");

          // si es casado y se encuentra una persona de tipo "conyuge aval"
          if (edo_civil_selected.id === edo_civil_casado?.id && person_representative?.spouse_id) {
            formik.setFieldValue("conyuge_id", person_representative.spouse_id);
          } else {
            formik.setFieldValue("conyuge_id", "");
          }
        }
        formik.setFieldValue("curp", person_representative?.curp || "");
        formik.setFieldValue("ocupacion", person_representative?.occupation || "");

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (String(formik.values.coloniaId) !== "-1") {
      formik.setFieldValue("otro", "");
    }
  }, [formik.values.coloniaId]);

  const handleOnClickRestart = () => {
    navigate("/producto/creditosimple/avance_expediente_digitalPFAE");
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box ml={3} mr={3}>
          <Box maxWidth="sm" mx="auto">
            <Typography
              mt={4}
              mb={1}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
            >
              Solicitud de crédito
            </Typography>
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={800}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
            >
              1{" "}
              <Typography
                component="span"
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
              >
                de 3
              </Typography>
            </Typography>

            <form onSubmit={formik.handleSubmit} noValidate>
              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.dark}
                fontWeight={600}
                my={1}
              >
                Información personal
              </Typography>

              <TextField
                id="curp"
                name="curp"
                label="CURP"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onChange={(e) => {
                  const input = e.target;
                  const value = e.target.value.toUpperCase();
                  const start = input.selectionStart;
                  formik.handleChange(e);
                  formik.setFieldValue("curp", value);
                  setTimeout(() => {
                    input.setSelectionRange(start, start);
                  }, 0);
                }}
                onBlur={formik.handleBlur}
                value={formik.values.curp}
                error={formik.touched.curp && Boolean(formik.errors.curp)}
                helperText={formik.touched.curp && formik.errors.curp}
                inputProps={{ maxLength: 18 }}
                disabled={isLoading || formik.isSubmitting}
              />

              <TextField
                id="ocupacion"
                name="ocupacion"
                label="Ocupación"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.ocupacion && Boolean(formik.errors.ocupacion)}
                helperText={formik.touched.ocupacion && formik.errors.ocupacion}
                value={formik.values.ocupacion}
                disabled={isLoading || formik.isSubmitting}
              />

              <FormControl
                fullWidth
                required
                sx={{
                  ...customStylesAsterisk,
                  mt: 2,
                }}
                error={formik.touched.estado_civil_id && Boolean(formik.errors.estado_civil_id)}
              >
                <InputLabel id="estado_civil_id">Estado civil</InputLabel>
                <Select
                  labelId="estado_civil_id"
                  id="estado_civil_id"
                  name="estado_civil_id"
                  fullWidth
                  required
                  label="Estado civil"
                  value={formik.values.estado_civil_id}
                  onBlur={savePersonalInformation}
                  onChange={formik.handleChange}
                  error={formik.touched.estado_civil_id && Boolean(formik.errors.estado_civil_id)}
                  disabled={isLoading || formik.isSubmitting}
                >
                  <MenuItem value={""}>Selecciona</MenuItem>
                  {listEdoCivil.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.estado_civil_id && formik.errors.estado_civil_id && (
                  <FormHelperText>{formik.errors.estado_civil_id}</FormHelperText>
                )}
              </FormControl>

              {formik.values.estado_civil_id !== "" && formik.values.estado_civil_id === idEdoCivilCasado && (
                <FormControl
                  fullWidth
                  required
                  sx={{
                    ...customStylesAsterisk,
                    mt: 2,
                  }}
                  error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
                >
                  <InputLabel id="matrimonial_regimes_id">Régimen matrimonial</InputLabel>
                  <Select
                    labelId="matrimonial_regimes_id"
                    id="matrimonial_regimes_id"
                    name="matrimonial_regimes_id"
                    fullWidth
                    required
                    label="Régimen matrimonial *"
                    value={formik.values.matrimonial_regimes_id}
                    onBlur={savePersonalInformation}
                    onChange={formik.handleChange}
                    error={formik.touched.matrimonial_regimes_id && Boolean(formik.errors.matrimonial_regimes_id)}
                    disabled={isLoading || formik.isSubmitting}
                  >
                    <MenuItem value={""}>Selecciona</MenuItem>
                    {listMatrimonialRegimen.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.matrimonial_regimes_id && formik.errors.matrimonial_regimes_id && (
                    <FormHelperText>{formik.errors.matrimonial_regimes_id}</FormHelperText>
                  )}
                </FormControl>
              )}

              {formik.values.estado_civil_id !== "" && formik.values.estado_civil_id === idEdoCivilCasado && (
                <React.Fragment>
                  <FormControl
                    fullWidth
                    required
                    sx={{
                      ...customStylesAsterisk,
                      mt: 2,
                    }}
                    error={formik.touched.conyuge_id && Boolean(formik.errors.conyuge_id)}
                  >
                    <InputLabel id="conyuge_id">Cónyuge</InputLabel>
                    <Select
                      labelId="conyuge_id"
                      id="conyuge_id"
                      name="conyuge_id"
                      fullWidth
                      required
                      label="Cónyuge"
                      value={formik.values.conyuge_id}
                      onBlur={savePersonalInformation}
                      onChange={formik.handleChange}
                      error={formik.touched.conyuge_id && Boolean(formik.errors.conyuge_id)}
                      disabled={isLoading || formik.isSubmitting}
                    >
                      <MenuItem value={""}>Selecciona</MenuItem>
                      {otherPersons.map((item: PersonaStructure) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name} {item.last_name} {item.last_name_2}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.conyuge_id && formik.errors.conyuge_id && (
                      <FormHelperText>{formik.errors.conyuge_id}</FormHelperText>
                    )}
                  </FormControl>

                  {formik.values.conyuge_id !== "" && formik.values.conyuge_id === OTHER_PERSON_ID && (
                    <React.Fragment>
                      <FormControl fullWidth>
                        <TextField
                          id="conyuge_name"
                          name="conyuge_name"
                          label="Nombre del cónyuge"
                          margin="normal"
                          required
                          fullWidth
                          autoComplete="off"
                          onBlur={savePersonalInformation}
                          onChange={formik.handleChange}
                          error={formik.touched.conyuge_name && Boolean(formik.errors.conyuge_name)}
                          helperText={formik.touched.conyuge_name && formik.errors.conyuge_name}
                          value={formik.values.conyuge_name}
                          disabled={isLoading || formik.isSubmitting}
                        />
                      </FormControl>

                      <FormControl fullWidth>
                        <TextField
                          id="conyuge_last_name"
                          name="conyuge_last_name"
                          label="Apellido paterno del cónyuge"
                          margin="normal"
                          required
                          fullWidth
                          autoComplete="off"
                          onBlur={savePersonalInformation}
                          onChange={formik.handleChange}
                          error={formik.touched.conyuge_last_name && Boolean(formik.errors.conyuge_last_name)}
                          helperText={formik.touched.conyuge_last_name && formik.errors.conyuge_last_name}
                          value={formik.values.conyuge_last_name}
                          disabled={isLoading || formik.isSubmitting}
                        />
                      </FormControl>

                      <FormControl fullWidth>
                        <TextField
                          id="conyuge_last_name_2"
                          name="conyuge_last_name_2"
                          label="Apellido materno del cónyuge"
                          margin="normal"
                          fullWidth
                          autoComplete="off"
                          onBlur={savePersonalInformation}
                          onChange={formik.handleChange}
                          error={formik.touched.conyuge_last_name_2 && Boolean(formik.errors.conyuge_last_name_2)}
                          helperText={formik.touched.conyuge_last_name_2 && formik.errors.conyuge_last_name_2}
                          value={formik.values.conyuge_last_name_2}
                          disabled={isLoading || formik.isSubmitting}
                        />
                      </FormControl>
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}

              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.dark}
                fontWeight={600}
                my={2}
              >
                Domicilio operativo:
              </Typography>

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="codigoPostal"
                  name="codigoPostal"
                  label="Código Postal"
                  required
                  value={formik.values.codigoPostal}
                  onChange={(e) => {
                    const cp = e.target.value.replace(/[^0-9]/g, "");
                    formik.setFieldValue("codigoPostal", cp);
                    if (cp.length === 5) {
                      fetchLocationData(cp);
                    }
                  }}
                  onBlur={saveOperativeAddress}
                  error={formik.touched.codigoPostal && Boolean(formik.errors.codigoPostal)}
                  helperText={formik.touched.codigoPostal ? formik.errors.codigoPostal : ""}
                  margin="normal"
                  disabled={isLoading || formik.isSubmitting}
                  inputProps={{ maxLength: 5 }}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="calle"
                  name="calle"
                  label="Calle o avenida"
                  required
                  value={formik.values.calle}
                  onChange={formik.handleChange}
                  onBlur={saveOperativeAddress}
                  error={formik.touched.calle && Boolean(formik.errors.calle)}
                  helperText={formik.touched.calle ? formik.errors.calle : ""}
                  margin="normal"
                  disabled={isLoading || formik.isSubmitting}
                  inputProps={{ maxLength: 100 }}
                />
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="noExterior"
                      name="noExterior"
                      label="No. Exterior"
                      required
                      value={formik.values.noExterior}
                      onChange={formik.handleChange}
                      onBlur={saveOperativeAddress}
                      error={formik.touched.noExterior && Boolean(formik.errors.noExterior)}
                      helperText={formik.touched.noExterior ? formik.errors.noExterior : ""}
                      margin="normal"
                      disabled={isLoading || formik.isSubmitting}
                      inputProps={{ maxLength: 20 }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="noInterior"
                      name="noInterior"
                      label="No. Interior"
                      value={formik.values.noInterior}
                      onChange={formik.handleChange}
                      onBlur={saveOperativeAddress}
                      error={formik.touched.noInterior && Boolean(formik.errors.noInterior)}
                      helperText={formik.touched.noInterior ? formik.errors.noInterior : ""}
                      margin="normal"
                      disabled={isLoading || formik.isSubmitting}
                      inputProps={{ maxLength: 20 }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl
                fullWidth
                required
                sx={{
                  ...customStylesAsterisk,
                  mt: 1,
                }}
                error={formik.touched.coloniaId && Boolean(formik.errors.coloniaId)}
              >
                <InputLabel id={"coloniaId"}>Colonia</InputLabel>
                <Select
                  labelId="coloniaId"
                  name="coloniaId"
                  id="coloniaId"
                  fullWidth
                  required
                  label="Colonia"
                  value={formik.values.coloniaId}
                  onBlur={saveOperativeAddress}
                  onChange={formik.handleChange}
                  error={formik.touched.coloniaId && Boolean(formik.errors.coloniaId)}
                  sx={{ textAlign: "left" }}
                  disabled={isLoading || formik.isSubmitting}
                >
                  <MenuItem value="">Selecciona</MenuItem>
                  {colonias.map((colonia) => (
                    <MenuItem key={colonia.id} value={colonia.id}>
                      {colonia.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.coloniaId && formik.errors.coloniaId && (
                  <FormHelperText>{formik.errors.coloniaId}</FormHelperText>
                )}
              </FormControl>

              {String(formik.values.coloniaId) === "-1" && (
                <>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12}>
                      <TextField
                        id="otro"
                        name="otro"
                        label="Otra Colonia"
                        margin="normal"
                        required
                        fullWidth
                        autoComplete="off"
                        onBlur={saveOperativeAddress}
                        onChange={formik.handleChange}
                        error={formik.touched.otro && Boolean(formik.errors.otro)}
                        helperText={formik.touched.otro && formik.errors.otro}
                        value={formik.values.otro}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="ciudad"
                  name="ciudad"
                  label="Ciudad"
                  value={formik.values.ciudad}
                  error={formik.touched.ciudadId && Boolean(formik.errors.ciudadId)}
                  helperText={formik.touched.ciudadId ? formik.errors.ciudadId : ""}
                  margin="normal"
                  disabled={true}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="localidadId"
                  name="localidadId"
                  label="Municipio"
                  value={formik.values.localidad}
                  error={formik.touched.localidadId && Boolean(formik.errors.localidadId)}
                  helperText={formik.touched.localidadId ? formik.errors.localidadId : ""}
                  margin="normal"
                  disabled={true}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="estado"
                  name="estado"
                  label="Entidad federativa"
                  value={formik.values.estado}
                  error={formik.touched.estadoId && Boolean(formik.errors.estadoId)}
                  helperText={formik.touched.estadoId ? formik.errors.estadoId : ""}
                  margin="normal"
                  disabled={true}
                />
              </FormControl>

              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.dark}
                fontWeight={600}
                my={2}
              >
                Domicilio Personal
              </Typography>

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="codigoPostal_particular"
                  name="codigoPostal_particular"
                  label="Código Postal"
                  required
                  value={formik.values.codigoPostal_particular}
                  onChange={(e) => {
                    const cp = e.target.value.replace(/[^0-9]/g, "");
                    formik.setFieldValue("codigoPostal_particular", cp);
                    if (cp.length === 5) {
                      fetchLocationDataParticular(cp);
                    }
                  }}
                  onBlur={savePersonalAddress}
                  error={formik.touched.codigoPostal_particular && Boolean(formik.errors.codigoPostal_particular)}
                  helperText={formik.touched.codigoPostal_particular ? formik.errors.codigoPostal_particular : ""}
                  margin="normal"
                  disabled={isLoading || formik.isSubmitting}
                  inputProps={{ maxLength: 5 }}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="calle_particular"
                  name="calle_particular"
                  label="Calle o avenida"
                  required
                  value={formik.values.calle_particular}
                  onChange={formik.handleChange}
                  onBlur={savePersonalAddress}
                  error={formik.touched.calle_particular && Boolean(formik.errors.calle_particular)}
                  helperText={formik.touched.calle_particular ? formik.errors.calle_particular : ""}
                  margin="normal"
                  disabled={isLoading || formik.isSubmitting}
                  inputProps={{ maxLength: 100 }}
                />
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="noExterior_particular"
                      name="noExterior_particular"
                      label="No. Exterior"
                      required
                      value={formik.values.noExterior_particular}
                      onChange={formik.handleChange}
                      onBlur={savePersonalAddress}
                      error={formik.touched.noExterior_particular && Boolean(formik.errors.noExterior_particular)}
                      helperText={formik.touched.noExterior_particular ? formik.errors.noExterior_particular : ""}
                      margin="normal"
                      disabled={isLoading || formik.isSubmitting}
                      inputProps={{ maxLength: 20 }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="noInterior_particular"
                      name="noInterior_particular"
                      label="No. Interior"
                      value={formik.values.noInterior_particular}
                      onChange={formik.handleChange}
                      onBlur={savePersonalAddress}
                      error={formik.touched.noInterior_particular && Boolean(formik.errors.noInterior_particular)}
                      helperText={formik.touched.noInterior_particular ? formik.errors.noInterior_particular : ""}
                      margin="normal"
                      disabled={isLoading || formik.isSubmitting}
                      inputProps={{ maxLength: 20 }}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl
                fullWidth
                required
                sx={{
                  ...customStylesAsterisk,
                  mt: 1,
                }}
                error={formik.touched.coloniaId_particular && Boolean(formik.errors.coloniaId_particular)}
              >
                <InputLabel id={"coloniaId_particular"}>Colonia</InputLabel>
                <Select
                  labelId="coloniaId_particular"
                  name="coloniaId_particular"
                  id="coloniaId_particular"
                  fullWidth
                  required
                  label="Colonia"
                  value={formik.values.coloniaId_particular}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.coloniaId_particular && Boolean(formik.errors.coloniaId_particular)}
                  sx={{ textAlign: "left" }}
                  disabled={isLoading || formik.isSubmitting}
                >
                  <MenuItem value="">Selecciona</MenuItem>
                  {coloniasParticular.map((colonia) => (
                    <MenuItem key={colonia.id} value={colonia.id}>
                      {colonia.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.coloniaId_particular && formik.errors.coloniaId_particular && (
                  <FormHelperText>{formik.errors.coloniaId_particular}</FormHelperText>
                )}
              </FormControl>

              {String(formik.values.coloniaId_particular) === "-1" && (
                <>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={12}>
                      <TextField
                        id="otro_particular"
                        name="otro_particular"
                        label="Otra Colonia"
                        margin="normal"
                        required
                        fullWidth
                        autoComplete="off"
                        onBlur={savePersonalAddress}
                        onChange={formik.handleChange}
                        error={formik.touched.otro_particular && Boolean(formik.errors.otro_particular)}
                        helperText={formik.touched.otro_particular && formik.errors.otro_particular}
                        value={formik.values.otro_particular}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="ciudad_particular"
                  name="ciudad_particular"
                  label="Ciudad"
                  value={formik.values.ciudad_particular}
                  error={formik.touched.ciudad_particular && Boolean(formik.errors.ciudad_particular)}
                  helperText={formik.touched.ciudad_particular ? formik.errors.ciudad_particular : ""}
                  margin="normal"
                  disabled={true}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="municipio_particular"
                  name="municipio_particular"
                  label="Municipio"
                  value={formik.values.localidad_particular}
                  error={formik.touched.localidadId_particular && Boolean(formik.errors.localidadId_particular)}
                  helperText={formik.touched.localidadId_particular ? formik.errors.localidadId_particular : ""}
                  margin="normal"
                  disabled={true}
                />
              </FormControl>

              <FormControl fullWidth>
                <TextField
                  fullWidth
                  id="estado_particular"
                  name="estado_particular"
                  label="Entidad federativa"
                  value={formik.values.estado_particular}
                  error={formik.touched.estado_particular && Boolean(formik.errors.estado_particular)}
                  helperText={formik.touched.estado_particular ? formik.errors.estado_particular : ""}
                  margin="normal"
                  disabled={true}
                />
              </FormControl>

              <Grid container columnSpacing={1} my={4}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="blue_outlined"
                    sx={{ padding: "0", height: "100%" }}
                    onClick={handleOnClickRestart}
                    disabled={formik.isSubmitting}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    color="primary"
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default SolicitudCreditoPart1PFAE;
